import { CheckIcon, CloseIcon } from "@chakra-ui/icons"
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
  Wrap,
  WrapItem,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { type Role, UsersService } from "../../../client"

export const Route = createFileRoute("/_layout/admin/roles")({
  component: RolesAdminPage,
})

type RoleFormState = {
  id?: number
  code: string
  level: string
  permissions: string[]
}

const defaultRoleState: RoleFormState = {
  code: "",
  level: "user",
  permissions: [],
}

/**
 * Maps role level to a consistent badge color used across the admin view.
 */
function getRoleLevelColor(level?: string) {
  switch ((level ?? "").toLowerCase()) {
    case "superadmin":
      return "purple"
    case "admin":
      return "blue"
    default:
      return "green"
  }
}

/**
 * Returns a user-facing explanation for each role level option.
 */
function getRoleLevelDescription(level: string) {
  switch (level) {
    case "superadmin":
      return "Pełny dostęp do konfiguracji systemu i uprawnień krytycznych."
    case "admin":
      return "Dostęp administracyjny do operacji i zarządzania użytkownikami."
    default:
      return "Dostęp operacyjny ograniczony do codziennej pracy użytkownika."
  }
}

/**
 * Maps permission codes to short user-facing scope labels.
 */
function getPermissionScopeLabel(code: string) {
  const [top, sub] = code.split(".")
  if (top === "wms" && sub) {
    const wmsLabels: Record<string, string> = {
      reception: "Przyjęcia",
      warehouse_order: "Zlecenia magazynowe",
      transfer_from_warehouse_order: "Przesunięcia ze zleceń",
      release: "Wydania",
      rw: "RW",
      pw: "PW",
      product: "Produkty",
      label_template: "Szablony etykiet",
      company_profile: "Profil firmy",
      contragent: "Kontrahenci",
      production_order: "Zlecenia produkcyjne",
      meldunek: "Meldunki",
      purchase_order: "Zamówienia zakupu",
      procurement_request: "Zapotrzebowania zakupowe",
      pzfrompurchaseorder: "PZ z zamówienia zakupu",
      sales_offer: "Oferty sprzedaży",
      sales_order: "Zamówienia sprzedaży",
      release_from_sales_order: "WZ z zamówienia sprzedaży",
      technology: "Technologie",
      auto_replenishment: "Automatyczne uzupełnianie",
      mmw: "MMW",
      mmp: "MMP",
      mmr: "MMR",
      transfer: "Przesunięcia",
      warehouse: "Magazyny",
      location_template: "Szablony lokacji",
    }
    return wmsLabels[sub] ?? "WMS"
  }
  if (top === "users") return "Użytkownicy"
  if (top === "lms") return "Licencje"
  if (top === "new_productions") return "Nowe produkcje"
  return top.replace(/_/g, " ")
}

/**
 * Toggles a permission code inside the editable role form.
 */
function togglePermissionSelection(
  permissions: string[],
  permissionCode: string,
) {
  const next = new Set(permissions)
  if (next.has(permissionCode)) {
    next.delete(permissionCode)
  } else {
    next.add(permissionCode)
  }
  return Array.from(next)
}

function RolesAdminPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const roleModal = useDisclosure()

  const [roleForm, setRoleForm] = useState<RoleFormState>(defaultRoleState)

  const rolesQuery = useQuery({
    queryKey: ["roles", "list"],
    queryFn: UsersService.readRoles,
  })

  const permissionsQuery = useQuery({
    queryKey: ["permissions", "list"],
    queryFn: UsersService.readPermissions,
  })

  const createRoleMutation = useMutation({
    mutationFn: (payload: RoleFormState) =>
      UsersService.createRole({
        code: payload.code,
        level: payload.level,
        permissions: payload.permissions,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles", "list"] })
      roleModal.onClose()
      setRoleForm(defaultRoleState)
      toast({
        title: "Rola utworzona",
        status: "success",
        duration: 3000,
        isClosable: true,
      })
    },
    onError: (error: any) => {
      toast({
        title: "Nie udało się utworzyć roli",
        description: error?.message ?? "Błąd",
        status: "error",
        duration: 4000,
        isClosable: true,
      })
    },
  })

  const updateRoleMutation = useMutation({
    mutationFn: (payload: RoleFormState) =>
      UsersService.updateRole({
        roleId: payload.id as number,
        requestBody: {
          code: payload.code,
          level: payload.level,
          permissions: payload.permissions,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles", "list"] })
      roleModal.onClose()
      setRoleForm(defaultRoleState)
      toast({
        title: "Rola zaktualizowana",
        status: "success",
        duration: 3000,
        isClosable: true,
      })
    },
    onError: (error: any) => {
      toast({
        title: "Nie udało się zaktualizować roli",
        description: error?.message ?? "Błąd",
        status: "error",
        duration: 4000,
        isClosable: true,
      })
    },
  })

  const deleteRoleMutation = useMutation({
    mutationFn: (roleId: number) => UsersService.deleteRole({ roleId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles", "list"] })
      toast({
        title: "Rola usunięta",
        status: "success",
        duration: 3000,
        isClosable: true,
      })
    },
    onError: (error: any) => {
      toast({
        title: "Nie udało się usunąć roli",
        description: error?.message ?? "Błąd",
        status: "error",
        duration: 4000,
        isClosable: true,
      })
    },
  })

  const handleOpenCreateRole = () => {
    setRoleForm(defaultRoleState)
    roleModal.onOpen()
  }

  const handleOpenEditRole = (role: Role) => {
    setRoleForm({
      id: role.id,
      code: role.code,
      level: role.level,
      permissions: (role.permissions ?? []).map((p) => p.code),
    })
    roleModal.onOpen()
  }

  const roles = rolesQuery.data ?? []
  const permissions = permissionsQuery.data ?? []

  const groupedPermissions = permissions.reduce(
    (acc, perm) => {
      const parts = perm.code.split(".")
      const top = parts[0] || "Inne"
      if (top === "wms") {
        const sub = parts[1] || "inne"
        if (!acc.wms[sub]) acc.wms[sub] = []
        acc.wms[sub].push(perm)
        return acc
      }
      if (!acc.general[top]) {
        acc.general[top] = []
      }
      acc.general[top].push(perm)
      return acc
    },
    {
      general: {} as Record<string, typeof permissions>,
      wms: {} as Record<string, typeof permissions>,
    },
  )

  // Ensure dashboard category is visible if it exists or if we want to encourage its use
  // If we want it to be ALWAYS there, we could pre-initialize it, but let's stick to dynamic
  // unless the user really wants a "block" even if empty.
  // Actually, the user says "missing dashboard block", which usually means they don't see it
  // because they haven't created the permissions.
  // I will add a small helper or just sort the categories so it's predictable.
  const sortedCategories = Object.keys(groupedPermissions.general).sort(
    (a, b) => {
      // Keep 'Inne' at the end
      if (a === "Inne") return 1
      if (b === "Inne") return -1
      return a.localeCompare(b)
    },
  )
  const sortedWmsCategories = Object.keys(groupedPermissions.wms).sort((a, b) =>
    a.localeCompare(b),
  )
  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const mutedText = useColorModeValue("gray.500", "gray.300")
  const sectionHeaderBg = useColorModeValue("gray.50", "whiteAlpha.50")
  const cardBg = useColorModeValue("white", "gray.900")
  const permissionCardBg = useColorModeValue("gray.50", "whiteAlpha.50")
  const modalHeroBg = useColorModeValue("blue.50", "whiteAlpha.100")
  const modalSectionBg = useColorModeValue("gray.50", "whiteAlpha.50")
  const selectableCardBg = useColorModeValue("white", "gray.800")
  const selectableCardBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const selectableCardHover = useColorModeValue("blue.50", "whiteAlpha.100")
  const selectedCardBg = useColorModeValue("green.50", "green.900")
  const selectedCardBorder = useColorModeValue("green.300", "green.400")
  const levelCardBg = useColorModeValue("white", "gray.800")
  const levelCardSelectedBg = useColorModeValue("blue.50", "blue.900")
  const levelCardBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const levelCardSelectedBorder = useColorModeValue("blue.300", "blue.400")

  return (
    <Container maxW="9xl" py={4}>
      <Stack spacing={4}>
        <Box
          bgGradient="linear(to-r, #2B6CB0, #0EA5A4)"
          color="white"
          borderRadius="2xl"
          px={{ base: 5, md: 8 }}
          py={{ base: 5, md: 7 }}
          boxShadow="lg"
        >
          <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
            <Box>
              <Heading size="lg">Role i uprawnienia</Heading>
              <Text mt={1} opacity={0.95}>
                Zarządzanie poziomami dostępu i politykami uprawnień systemu.
              </Text>
            </Box>
            <HStack>
              <Button
                size="sm"
                colorScheme="green"
                onClick={handleOpenCreateRole}
              >
                Dodaj rolę
              </Button>
            </HStack>
          </Flex>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
          <Box
            bg={panelBg}
            borderWidth="1px"
            borderColor={panelBorder}
            borderRadius="xl"
            px={4}
            py={3}
          >
            <Text color={mutedText} fontSize="sm">
              Liczba ról
            </Text>
            <Text fontSize="2xl" fontWeight="bold">
              {roles.length}
            </Text>
          </Box>
          <Box
            bg={panelBg}
            borderWidth="1px"
            borderColor={panelBorder}
            borderRadius="xl"
            px={4}
            py={3}
          >
            <Text color={mutedText} fontSize="sm">
              Liczba uprawnień
            </Text>
            <Text fontSize="2xl" fontWeight="bold">
              {permissions.length}
            </Text>
          </Box>
          <Box
            bg={panelBg}
            borderWidth="1px"
            borderColor={panelBorder}
            borderRadius="xl"
            px={4}
            py={3}
          >
            <Text color={mutedText} fontSize="sm">
              Kategorie WMS
            </Text>
            <Text fontSize="2xl" fontWeight="bold">
              {sortedWmsCategories.length}
            </Text>
          </Box>
        </SimpleGrid>

        <Box
          bg={panelBg}
          borderWidth="1px"
          borderColor={panelBorder}
          borderRadius="2xl"
          overflow="hidden"
          mb={8}
        >
          <Flex
            px={{ base: 4, md: 6 }}
            py={{ base: 4, md: 5 }}
            borderBottomWidth="1px"
            borderColor={panelBorder}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={3}
            bg={sectionHeaderBg}
          >
            <Box>
              <Text fontWeight="semibold" fontSize="lg">
                Role
              </Text>
              <Text fontSize="sm" color={mutedText} mt={1}>
                Poziomy dostępu z przypisanymi zestawami uprawnień.
              </Text>
            </Box>
            <Badge
              colorScheme="blue"
              variant="subtle"
              px={3}
              py={1}
              borderRadius="full"
            >
              {roles.length} aktywnych ról
            </Badge>
          </Flex>
          {rolesQuery.isPending ? (
            <Flex justify="center" align="center" py={10}>
              <Spinner size="lg" color="main.accent" />
            </Flex>
          ) : roles.length === 0 ? (
            <Box px={6} py={8} color="gray.500">
              Brak ról
            </Box>
          ) : (
            <SimpleGrid
              columns={{ base: 1, xl: 2 }}
              spacing={4}
              p={{ base: 4, md: 6 }}
            >
              {roles.map((role) => {
                const rolePermissions = role.permissions ?? []
                const permissionPreview = rolePermissions.slice(0, 4)
                const hiddenPermissionsCount = Math.max(
                  rolePermissions.length - permissionPreview.length,
                  0,
                )
                const permissionScopes = Object.entries(
                  rolePermissions.reduce(
                    (acc, perm) => {
                      const label = getPermissionScopeLabel(perm.code)
                      acc[label] = (acc[label] ?? 0) + 1
                      return acc
                    },
                    {} as Record<string, number>,
                  ),
                )
                  .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
                  .slice(0, 3)
                return (
                  <Box
                    key={role.id ?? role.code}
                    borderWidth="1px"
                    borderColor={panelBorder}
                    borderRadius="xl"
                    bg={cardBg}
                    p={5}
                    boxShadow="sm"
                  >
                    <Flex justify="space-between" align="flex-start" gap={4}>
                      <Stack spacing={2} flex="1">
                        <HStack spacing={2} wrap="wrap">
                          <Heading size="sm">{role.code}</Heading>
                          <Badge
                            colorScheme={getRoleLevelColor(role.level)}
                            variant="subtle"
                            borderRadius="full"
                            px={2.5}
                            py={1}
                          >
                            {role.level}
                          </Badge>
                        </HStack>
                        <Text fontSize="sm" color={mutedText}>
                          Rola kontroluje dostęp do modułów administracyjnych i
                          operacyjnych przypisanych poniżej.
                        </Text>
                      </Stack>
                      <Badge
                        colorScheme={
                          rolePermissions.length > 0 ? "green" : "gray"
                        }
                        variant="outline"
                        borderRadius="full"
                        px={2.5}
                        py={1}
                      >
                        {rolePermissions.length} uprawnień
                      </Badge>
                    </Flex>

                    <SimpleGrid columns={2} spacing={3} mt={4}>
                      <Box
                        borderWidth="1px"
                        borderColor={panelBorder}
                        borderRadius="lg"
                        px={3}
                        py={2.5}
                      >
                        <Text
                          fontSize="xs"
                          color={mutedText}
                          textTransform="uppercase"
                        >
                          Poziom
                        </Text>
                        <Text fontWeight="semibold" mt={1}>
                          {role.level}
                        </Text>
                      </Box>
                      <Box
                        borderWidth="1px"
                        borderColor={panelBorder}
                        borderRadius="lg"
                        px={3}
                        py={2.5}
                      >
                        <Text
                          fontSize="xs"
                          color={mutedText}
                          textTransform="uppercase"
                        >
                          Zakres
                        </Text>
                        <Text fontWeight="semibold" mt={1}>
                          {rolePermissions.length > 0
                            ? "Dostęp skonfigurowany"
                            : "Brak dostępu"}
                        </Text>
                      </Box>
                    </SimpleGrid>

                    <Divider my={4} />

                    <Stack spacing={3}>
                      <Flex justify="space-between" align="center" gap={3}>
                        <Text fontSize="sm" fontWeight="medium">
                          Przypisane uprawnienia
                        </Text>
                        {hiddenPermissionsCount > 0 && (
                          <Text fontSize="xs" color={mutedText}>
                            Widok skrócony
                          </Text>
                        )}
                      </Flex>
                      {rolePermissions.length > 0 ? (
                        <Stack spacing={3}>
                          <Wrap spacing={2}>
                            {permissionScopes.map(([label, count]) => (
                              <WrapItem key={label}>
                                <Badge
                                  colorScheme="purple"
                                  variant="subtle"
                                  px={2.5}
                                  py={1}
                                  borderRadius="full"
                                >
                                  {label} ({count})
                                </Badge>
                              </WrapItem>
                            ))}
                          </Wrap>
                          <Wrap spacing={2}>
                            {permissionPreview.map((perm) => (
                              <WrapItem key={perm.code}>
                                <Badge
                                  colorScheme="blue"
                                  variant="subtle"
                                  px={2.5}
                                  py={1.5}
                                  borderRadius="md"
                                >
                                  {perm.name}
                                </Badge>
                              </WrapItem>
                            ))}
                            {hiddenPermissionsCount > 0 && (
                              <WrapItem>
                                <Badge
                                  colorScheme="gray"
                                  variant="outline"
                                  px={2.5}
                                  py={1.5}
                                  borderRadius="md"
                                >
                                  +{hiddenPermissionsCount} więcej
                                </Badge>
                              </WrapItem>
                            )}
                          </Wrap>
                        </Stack>
                      ) : (
                        <Text fontSize="sm" color="gray.400">
                          Brak przypisanych uprawnień
                        </Text>
                      )}
                    </Stack>

                    <HStack mt={5} spacing={3}>
                      <Button
                        size="sm"
                        onClick={() => handleOpenEditRole(role)}
                      >
                        Edytuj
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={() =>
                          role.id && deleteRoleMutation.mutate(role.id)
                        }
                      >
                        Usuń
                      </Button>
                    </HStack>
                  </Box>
                )
              })}
            </SimpleGrid>
          )}
        </Box>

        <Box
          bg={panelBg}
          borderWidth="1px"
          borderColor={panelBorder}
          borderRadius="2xl"
          overflow="hidden"
        >
          <Flex
            px={{ base: 4, md: 6 }}
            py={{ base: 4, md: 5 }}
            borderBottomWidth="1px"
            borderColor={panelBorder}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={3}
            bg={sectionHeaderBg}
          >
            <Box>
              <Text fontWeight="semibold" fontSize="lg">
                Uprawnienia
              </Text>
              <Text fontSize="sm" color={mutedText} mt={1}>
                Katalog granularnych akcji podzielonych na obszary systemu.
              </Text>
            </Box>
            <HStack spacing={2}>
              <Badge
                colorScheme="teal"
                variant="subtle"
                px={3}
                py={1}
                borderRadius="full"
              >
                {Object.keys(groupedPermissions.general).length} kategorii
                ogólnych
              </Badge>
              <Badge
                colorScheme="cyan"
                variant="subtle"
                px={3}
                py={1}
                borderRadius="full"
              >
                {sortedWmsCategories.length} sekcji WMS
              </Badge>
            </HStack>
          </Flex>
          {permissionsQuery.isPending ? (
            <Flex justify="center" align="center" py={10}>
              <Spinner size="lg" color="main.accent" />
            </Flex>
          ) : (
            <Accordion allowMultiple defaultIndex={[0]}>
              {permissions.length === 0 ? (
                <Box px={4} py={6} color="gray.500">
                  Brak uprawnień
                </Box>
              ) : (
                <>
                  {sortedCategories.map((category) => {
                    const perms = groupedPermissions.general[category]
                    return (
                      <AccordionItem key={category}>
                        <h2>
                          <AccordionButton>
                            <Box
                              as="span"
                              flex="1"
                              textAlign="left"
                              fontWeight="bold"
                            >
                              {category.toUpperCase()} ({perms.length})
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                          <Stack spacing={3}>
                            {perms.map((perm) => (
                              <Box
                                key={perm.code}
                                px={4}
                                py={4}
                                borderWidth="1px"
                                borderColor={panelBorder}
                                borderRadius="xl"
                                bg={permissionCardBg}
                              >
                                <Flex
                                  justify="space-between"
                                  align={{ base: "flex-start", md: "center" }}
                                  direction={{ base: "column", md: "row" }}
                                  gap={3}
                                >
                                  <Stack spacing={1} flex="1">
                                    <Text fontWeight="semibold">
                                      {perm.name}
                                    </Text>
                                    {perm.description && (
                                      <Text fontSize="sm" color="gray.500">
                                        {perm.description}
                                      </Text>
                                    )}
                                  </Stack>
                                </Flex>
                              </Box>
                            ))}
                          </Stack>
                        </AccordionPanel>
                      </AccordionItem>
                    )
                  })}
                  {sortedWmsCategories.length > 0 && (
                    <AccordionItem key="wms">
                      <h2>
                        <AccordionButton>
                          <Box
                            as="span"
                            flex="1"
                            textAlign="left"
                            fontWeight="bold"
                          >
                            WMS ({sortedWmsCategories.length})
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={4}>
                        <Accordion allowMultiple defaultIndex={[0]}>
                          {sortedWmsCategories.map((sub) => {
                            const perms = groupedPermissions.wms[sub]
                            return (
                              <AccordionItem key={sub} border="none" mb={2}>
                                <h3>
                                  <AccordionButton
                                    px={0}
                                    _hover={{ bg: "transparent" }}
                                  >
                                    <Box
                                      as="span"
                                      flex="1"
                                      textAlign="left"
                                      fontWeight="bold"
                                      textTransform="uppercase"
                                      fontSize="xs"
                                      color="gray.500"
                                    >
                                      {getPermissionScopeLabel(`wms.${sub}`)} (
                                      {perms.length})
                                    </Box>
                                    <AccordionIcon />
                                  </AccordionButton>
                                </h3>
                                <AccordionPanel pb={4} px={0}>
                                  <Stack spacing={3}>
                                    {perms.map((perm) => (
                                      <Box
                                        key={perm.code}
                                        px={4}
                                        py={4}
                                        borderWidth="1px"
                                        borderColor={panelBorder}
                                        borderRadius="xl"
                                        bg={permissionCardBg}
                                      >
                                        <Flex
                                          justify="space-between"
                                          align={{
                                            base: "flex-start",
                                            md: "center",
                                          }}
                                          direction={{
                                            base: "column",
                                            md: "row",
                                          }}
                                          gap={3}
                                        >
                                          <Stack spacing={1} flex="1">
                                            <Text fontWeight="semibold">
                                              {perm.name}
                                            </Text>
                                            {perm.description && (
                                              <Text
                                                fontSize="sm"
                                                color="gray.500"
                                              >
                                                {perm.description}
                                              </Text>
                                            )}
                                          </Stack>
                                        </Flex>
                                      </Box>
                                    ))}
                                  </Stack>
                                </AccordionPanel>
                              </AccordionItem>
                            )
                          })}
                        </Accordion>
                      </AccordionPanel>
                    </AccordionItem>
                  )}
                </>
              )}
            </Accordion>
          )}
        </Box>

        <Modal isOpen={roleModal.isOpen} onClose={roleModal.onClose} size="4xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader
              borderBottomWidth="1px"
              borderBottomColor={panelBorder}
            >
              {roleForm.id ? "Edytuj rolę" : "Nowa rola"}
            </ModalHeader>
            <ModalBody pb={6}>
              <Stack spacing={6}>
                <Box
                  borderWidth="1px"
                  borderColor={panelBorder}
                  borderRadius="2xl"
                  bg={modalHeroBg}
                  px={{ base: 4, md: 5 }}
                  py={{ base: 4, md: 5 }}
                >
                  <Flex
                    justify="space-between"
                    align={{ base: "flex-start", md: "center" }}
                    direction={{ base: "column", md: "row" }}
                    gap={4}
                  >
                    <Box>
                      <Text fontSize="lg" fontWeight="semibold">
                        {roleForm.id
                          ? "Konfiguracja istniejącej roli"
                          : "Skonfiguruj nową rolę dostępu"}
                      </Text>
                      <Text fontSize="sm" color={mutedText} mt={1}>
                        Zdefiniuj poziom roli i wybierz zestaw uprawnień, który
                        będzie przypisany użytkownikom.
                      </Text>
                    </Box>
                    <HStack spacing={2} wrap="wrap">
                      <Badge
                        colorScheme={getRoleLevelColor(roleForm.level)}
                        variant="subtle"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        {roleForm.level}
                      </Badge>
                      <Badge
                        colorScheme="green"
                        variant="subtle"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        {roleForm.permissions.length} wybranych
                      </Badge>
                    </HStack>
                  </Flex>
                </Box>

                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Kod roli</FormLabel>
                    <Input
                      size="sm"
                      value={roleForm.code}
                      onChange={(e) =>
                        setRoleForm((prev) => ({
                          ...prev,
                          code: e.target.value,
                        }))
                      }
                      placeholder="np. LMS_VIEWER"
                    />
                    <Text fontSize="xs" color={mutedText} mt={1.5}>
                      Techniczny identyfikator roli używany przez system.
                    </Text>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Poziom</FormLabel>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                      {["user", "admin", "superadmin"].map((level) => {
                        const isSelected = roleForm.level === level
                        return (
                          <Box
                            key={level}
                            borderWidth="1px"
                            borderColor={
                              isSelected
                                ? levelCardSelectedBorder
                                : levelCardBorder
                            }
                            borderRadius="xl"
                            px={4}
                            py={3}
                            bg={isSelected ? levelCardSelectedBg : levelCardBg}
                            cursor="pointer"
                            transition="all 0.15s ease"
                            _hover={{
                              borderColor: isSelected
                                ? levelCardSelectedBorder
                                : "blue.300",
                            }}
                            onClick={() =>
                              setRoleForm((prev) => ({
                                ...prev,
                                level,
                              }))
                            }
                          >
                            <Stack spacing={2} align="flex-start">
                              <Text
                                fontWeight="semibold"
                                textTransform="capitalize"
                              >
                                {level}
                              </Text>
                              <Badge
                                colorScheme={
                                  isSelected ? getRoleLevelColor(level) : "gray"
                                }
                                variant={isSelected ? "solid" : "subtle"}
                                borderRadius="full"
                                px={2.5}
                                py={1}
                              >
                                {isSelected ? "Wybrano" : "Wybierz"}
                              </Badge>
                              <Text fontSize="sm" color={mutedText}>
                                {getRoleLevelDescription(level)}
                              </Text>
                            </Stack>
                          </Box>
                        )
                      })}
                    </SimpleGrid>
                    <Text fontSize="xs" color={mutedText} mt={1.5}>
                      Określa bazowy poziom dostępu administracyjnego.
                    </Text>
                  </FormControl>
                </SimpleGrid>

                <Box
                  borderWidth="1px"
                  borderColor={panelBorder}
                  borderRadius="2xl"
                  bg={modalSectionBg}
                  px={{ base: 4, md: 5 }}
                  py={{ base: 4, md: 5 }}
                >
                  <Flex
                    justify="space-between"
                    align={{ base: "flex-start", md: "center" }}
                    direction={{ base: "column", md: "row" }}
                    gap={3}
                    mb={4}
                  >
                    <Box>
                      <FormLabel mb={1}>Uprawnienia</FormLabel>
                      <Text fontSize="sm" color={mutedText}>
                        Wybierz tylko te akcje, które dana rola ma realnie
                        wykonywać w systemie.
                      </Text>
                    </Box>
                    <Badge
                      colorScheme="blue"
                      variant="subtle"
                      px={3}
                      py={1}
                      borderRadius="full"
                    >
                      {permissions.length} dostępnych
                    </Badge>
                  </Flex>
                  {permissions.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">
                      Brak zdefiniowanych uprawnień
                    </Text>
                  ) : (
                    <Accordion allowMultiple defaultIndex={[0]}>
                      {sortedCategories.map((category) => {
                        const perms = groupedPermissions.general[category]
                        return (
                          <AccordionItem key={category} border="none" mb={2}>
                            <h2>
                              <AccordionButton
                                px={0}
                                _hover={{ bg: "transparent" }}
                              >
                                <Box
                                  as="span"
                                  flex="1"
                                  textAlign="left"
                                  fontWeight="bold"
                                  textTransform="uppercase"
                                  fontSize="xs"
                                  color="gray.500"
                                >
                                  {category} ({perms.length})
                                </Box>
                                <AccordionIcon />
                              </AccordionButton>
                            </h2>
                            <AccordionPanel pb={4} px={0}>
                              <SimpleGrid
                                columns={{ base: 1, md: 2 }}
                                spacing={3}
                              >
                                {perms.map((perm) => {
                                  const isSelected =
                                    roleForm.permissions.includes(perm.code)
                                  return (
                                    <Box
                                      key={perm.code}
                                      borderWidth="1px"
                                      borderColor={
                                        isSelected
                                          ? selectedCardBorder
                                          : selectableCardBorder
                                      }
                                      borderRadius="xl"
                                      px={4}
                                      py={3}
                                      cursor="pointer"
                                      bg={
                                        isSelected
                                          ? selectedCardBg
                                          : selectableCardBg
                                      }
                                      transition="all 0.15s ease"
                                      _hover={{
                                        borderColor: isSelected
                                          ? selectedCardBorder
                                          : "blue.300",
                                        bg: isSelected
                                          ? selectedCardBg
                                          : selectableCardHover,
                                      }}
                                      onClick={() =>
                                        setRoleForm((prev) => ({
                                          ...prev,
                                          permissions:
                                            togglePermissionSelection(
                                              prev.permissions,
                                              perm.code,
                                            ),
                                        }))
                                      }
                                    >
                                      <Flex
                                        justify="space-between"
                                        align="flex-start"
                                        gap={3}
                                      >
                                        <Stack spacing={1} flex="1">
                                          <Text
                                            fontSize="sm"
                                            fontWeight="semibold"
                                          >
                                            {perm.description || perm.name}
                                          </Text>
                                          <Text fontSize="xs" color={mutedText}>
                                            {perm.name}
                                          </Text>
                                        </Stack>
                                        <Badge
                                          colorScheme={
                                            isSelected ? "green" : "gray"
                                          }
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="center"
                                          minW="26px"
                                          h="26px"
                                          borderRadius="full"
                                          flexShrink={0}
                                        >
                                          {isSelected ? (
                                            <CheckIcon boxSize={3} />
                                          ) : (
                                            <CloseIcon boxSize={2} />
                                          )}
                                        </Badge>
                                      </Flex>
                                    </Box>
                                  )
                                })}
                              </SimpleGrid>
                            </AccordionPanel>
                          </AccordionItem>
                        )
                      })}
                      {sortedWmsCategories.length > 0 && (
                        <AccordionItem key="wms" border="none" mb={2}>
                          <h2>
                            <AccordionButton
                              px={0}
                              _hover={{ bg: "transparent" }}
                            >
                              <Box
                                as="span"
                                flex="1"
                                textAlign="left"
                                fontWeight="bold"
                                textTransform="uppercase"
                                fontSize="xs"
                                color="gray.500"
                              >
                                WMS ({sortedWmsCategories.length})
                              </Box>
                              <AccordionIcon />
                            </AccordionButton>
                          </h2>
                          <AccordionPanel pb={4} px={0}>
                            <Accordion allowMultiple defaultIndex={[0]}>
                              {sortedWmsCategories.map((sub) => {
                                const perms = groupedPermissions.wms[sub]
                                return (
                                  <AccordionItem key={sub} border="none" mb={2}>
                                    <h3>
                                      <AccordionButton
                                        px={0}
                                        _hover={{ bg: "transparent" }}
                                      >
                                        <Box
                                          as="span"
                                          flex="1"
                                          textAlign="left"
                                          fontWeight="bold"
                                          textTransform="uppercase"
                                          fontSize="xs"
                                          color="gray.500"
                                        >
                                          {getPermissionScopeLabel(
                                            `wms.${sub}`,
                                          )}{" "}
                                          ({perms.length})
                                        </Box>
                                        <AccordionIcon />
                                      </AccordionButton>
                                    </h3>
                                    <AccordionPanel pb={4} px={0}>
                                      <SimpleGrid
                                        columns={{ base: 1, md: 2 }}
                                        spacing={3}
                                      >
                                        {perms.map((perm) => {
                                          const isSelected =
                                            roleForm.permissions.includes(
                                              perm.code,
                                            )
                                          return (
                                            <Box
                                              key={perm.code}
                                              borderWidth="1px"
                                              borderColor={
                                                isSelected
                                                  ? selectedCardBorder
                                                  : selectableCardBorder
                                              }
                                              borderRadius="xl"
                                              px={4}
                                              py={3}
                                              cursor="pointer"
                                              bg={
                                                isSelected
                                                  ? selectedCardBg
                                                  : selectableCardBg
                                              }
                                              transition="all 0.15s ease"
                                              _hover={{
                                                borderColor: isSelected
                                                  ? selectedCardBorder
                                                  : "blue.300",
                                                bg: isSelected
                                                  ? selectedCardBg
                                                  : selectableCardHover,
                                              }}
                                              onClick={() =>
                                                setRoleForm((prev) => ({
                                                  ...prev,
                                                  permissions:
                                                    togglePermissionSelection(
                                                      prev.permissions,
                                                      perm.code,
                                                    ),
                                                }))
                                              }
                                            >
                                              <Flex
                                                justify="space-between"
                                                align="flex-start"
                                                gap={3}
                                              >
                                                <Stack spacing={1} flex="1">
                                                  <Text
                                                    fontSize="sm"
                                                    fontWeight="semibold"
                                                  >
                                                    {perm.description ||
                                                      perm.name}
                                                  </Text>
                                                  <Text
                                                    fontSize="xs"
                                                    color={mutedText}
                                                  >
                                                    {perm.name}
                                                  </Text>
                                                </Stack>
                                                <Badge
                                                  colorScheme={
                                                    isSelected
                                                      ? "green"
                                                      : "gray"
                                                  }
                                                  display="flex"
                                                  alignItems="center"
                                                  justifyContent="center"
                                                  minW="26px"
                                                  h="26px"
                                                  borderRadius="full"
                                                  flexShrink={0}
                                                >
                                                  {isSelected ? (
                                                    <CheckIcon boxSize={3} />
                                                  ) : (
                                                    <CloseIcon boxSize={2} />
                                                  )}
                                                </Badge>
                                              </Flex>
                                            </Box>
                                          )
                                        })}
                                      </SimpleGrid>
                                    </AccordionPanel>
                                  </AccordionItem>
                                )
                              })}
                            </Accordion>
                          </AccordionPanel>
                        </AccordionItem>
                      )}
                    </Accordion>
                  )}
                </Box>
              </Stack>
            </ModalBody>
            <ModalFooter
              gap={3}
              borderTopWidth="1px"
              borderTopColor={panelBorder}
            >
              <Button
                size="sm"
                variant="ghost"
                onClick={roleModal.onClose}
                mr={3}
              >
                Anuluj
              </Button>
              <Button
                size="sm"
                colorScheme="green"
                onClick={() => {
                  if (roleForm.id) {
                    updateRoleMutation.mutate(roleForm)
                  } else {
                    createRoleMutation.mutate(roleForm)
                  }
                }}
                isLoading={
                  createRoleMutation.isPending || updateRoleMutation.isPending
                }
              >
                Zapisz
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Stack>
    </Container>
  )
}
